import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

export const Route = createFileRoute("/admin/configuracoes")({
  component: AdminConfiguracoes,
});

function AdminConfiguracoes() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const { data: configs = [] } = useQuery({
    queryKey: ["admin-configs"],
    queryFn: async () => {
      const { data } = await supabase.from("configuracoes").select("*");
      return data || [];
    },
  });

  const [form, setForm] = useState({
    ga_id: configs.find(c => c.chave === "ga_id")?.valor || "",
    gtm_id: configs.find(c => c.chave === "gtm_id")?.valor || "",
    pixel_id: configs.find(c => c.chave === "pixel_id")?.valor || "",
    search_console: configs.find(c => c.chave === "search_console")?.valor || "",
    custom_head: configs.find(c => c.chave === "custom_head")?.valor || "",
  });

  const updateConfig = useMutation({
    mutationFn: async ({ chave, valor }: { chave: string; valor: string }) => {
      const { error } = await supabase
        .from("configuracoes")
        .upsert({ 
          chave, 
          valor, 
          atualizado_em: new Date().toISOString(),
          atualizado_por: user?.id 
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-configs"] });
      toast.success("Salvo!");
    },
    onError: () => toast.error("Erro ao salvar."),
  });

  const save = (key: keyof typeof form) => updateConfig.mutate({ chave: key, valor: form[key] });

  const validateGa = (v: string) => !v || /^G-[A-Z0-9]{10}$/.test(v);
  const validateGtm = (v: string) => !v || /^GTM-[A-Z0-9]{4,10}$/.test(v);
  const validatePixel = (v: string) => !v || /^\d+$/.test(v);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Analytics e rastreamento</h1>
      
      <div className="grid gap-6">
        {[
          { key: "ga_id", label: "Google Analytics 4 (ID de medição)", help: "Formato: G-XXXXXXXXXX", validate: validateGa, placeholder: "G-XXXXXXXXXX" },
          { key: "gtm_id", label: "Google Tag Manager (ID do contêiner)", help: "Formato: GTM-XXXXXXX", validate: validateGtm, placeholder: "GTM-XXXXXXX" },
          { key: "pixel_id", label: "Meta Pixel (Apenas números)", help: "ID numérico do seu Pixel", validate: validatePixel, placeholder: "123456789" },
          { key: "search_console", label: "Google Search Console (Código de verificação)", help: "Conteúdo da tag de verificação", validate: undefined, placeholder: "Conteúdo da tag" },
        ].map(({ key, label, help, validate, placeholder }) => (
          <Card key={key} className="rounded-2xl border-border shadow-sm">
            <CardContent className="pt-6 space-y-2">
              <label className="text-sm font-medium">{label}</label>
              <div className="flex gap-2">
                <Input 
                  value={form[key as keyof typeof form]} 
                  onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className={validate && !validate(form[key as keyof typeof form]) ? "border-red-500" : ""}
                />
                <Button onClick={() => save(key as keyof typeof form)} variant="onLight">Salvar</Button>
                <Button onClick={() => setForm(f => ({ ...f, [key]: "" }))} variant="outline">Limpar</Button>
              </div>
              <p className="text-xs text-muted-foreground">{help}</p>
            </CardContent>
          </Card>
        ))}

        <Collapsible>
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:underline">
            Código personalizado no head <ChevronDown className="w-4 h-4" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <Card className="border-red-200">
              <CardHeader><CardTitle className="text-red-600 text-sm">Atenção: Uso avançado</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-red-500">Código inserido aqui roda em todas as páginas públicas. Use apenas se souber exatamente o que está fazendo.</p>
                <Textarea 
                  value={form.custom_head} 
                  onChange={(e) => setForm(f => ({ ...f, custom_head: e.target.value }))}
                  className="font-mono text-xs min-h-[120px]"
                />
                <Button onClick={() => save("custom_head")} className="w-full" variant="destructive">Salvar Código</Button>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}